import { BridgeClient } from 'app/service/bridge/BridgeClient';
import { normalizeAgentMcpUsername } from './credentialHandoff';

const SUCCESS_RESPONSE_KEYS = ['agent_mcp', 'result'];
const STATE_RESPONSE_KEYS = [
  'configured',
  'enabled',
  'endpoint',
  'password_configured',
  'ready',
  'status',
  'transport',
  'username',
];
const ERROR_RESPONSE_KEYS = ['code', 'error', 'result'];
const TRUSTED_ERROR_CODES = new Set([
  'UNKNOWN_ACTION',
  'INVALID_PAYLOAD',
  'INSECURE_TRANSPORT',
  'INVALID_CREDENTIALS',
  'PERSISTENCE_FAILED',
  'CREDENTIALS_REQUIRED',
]);
const LOOPBACK_HTTP_AUTHORITY = /^(?:localhost|127\.0\.0\.1|\[::1\])(?::[0-9]+)?$/i;

function isRecord(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function hasExactKeys(value, expectedKeys) {
  if (!isRecord(value)) return false;

  return Object.keys(value).sort().join('\u0000') === expectedKeys.slice().sort().join('\u0000');
}

function invalidStateError() {
  const error = new Error('The plugin returned an invalid AI access state.');
  error.code = 'INVALID_AGENT_MCP_STATE';
  return error;
}

function requestError(code) {
  const error = new Error('AI access request failed.');
  error.code = code;
  return error;
}

function parseSafeEndpoint(endpoint) {
  if (typeof endpoint !== 'string' || endpoint.length < 8 || endpoint.length > 2048) {
    return null;
  }

  try {
    const parsed = new URL(endpoint);
    if (
      (parsed.protocol !== 'https:' && parsed.protocol !== 'http:')
      || parsed.username
      || parsed.password
      || parsed.search
      || parsed.hash
    ) {
      return null;
    }

    return parsed;
  } catch (_error) {
    return null;
  }
}

function rawAuthority(endpoint) {
  const match = String(endpoint).match(/^[a-z][a-z0-9+.-]*:\/\/([^/?#]+)/i);
  return match ? match[1] : '';
}

export function deriveAgentMcpTransport(endpoint) {
  const parsed = parseSafeEndpoint(endpoint);
  if (!parsed) return 'invalid';
  if (parsed.protocol === 'https:') return 'https';

  return LOOPBACK_HTTP_AUTHORITY.test(rawAuthority(endpoint))
    ? 'local_http'
    : 'blocked_http';
}

function expectedStateStatus({ configured, enabled, transport }) {
  const transportSupported = transport === 'https' || transport === 'local_http';
  if (!transportSupported) return 'transport_blocked';
  if (configured && enabled) return 'ready';
  if (configured) return 'disabled';
  return 'needs_credentials';
}

function normalizeErrorResponse(response) {
  if (
    !hasExactKeys(response, ERROR_RESPONSE_KEYS)
    || response.result !== 'error'
    || typeof response.code !== 'string'
    || !TRUSTED_ERROR_CODES.has(response.code)
    || typeof response.error !== 'string'
    || response.error.length === 0
    || response.error.length > 1024
  ) {
    throw invalidStateError();
  }

  throw requestError(response.code);
}

function unwrapBridgeResponse(response) {
  if (!isRecord(response) || !Object.prototype.hasOwnProperty.call(response, 'success')) {
    return response;
  }

  // The bridge's replySuccess adds transport metadata to the plugin payload.
  // Only that known marker is removed; the product contract remains exact.
  if (response.success !== true) throw invalidStateError();
  const { success, ...payload } = response;
  return payload;
}

export function normalizeAgentMcpState(rawResponse) {
  const response = unwrapBridgeResponse(rawResponse);
  if (isRecord(response) && response.result === 'error') {
    return normalizeErrorResponse(response);
  }

  if (!hasExactKeys(response, SUCCESS_RESPONSE_KEYS) || response.result !== 'ok') {
    throw invalidStateError();
  }

  const state = response.agent_mcp;
  if (
    !hasExactKeys(state, STATE_RESPONSE_KEYS)
    || typeof state.configured !== 'boolean'
    || typeof state.enabled !== 'boolean'
    || typeof state.ready !== 'boolean'
    || typeof state.password_configured !== 'boolean'
    || typeof state.username !== 'string'
    || !/^[a-z0-9][a-z0-9._-]{2,63}$/.test(state.username)
    || typeof state.endpoint !== 'string'
    || typeof state.transport !== 'string'
  ) {
    throw invalidStateError();
  }

  const derivedTransport = deriveAgentMcpTransport(state.endpoint);
  const expectedStatus = expectedStateStatus({
    configured: state.configured,
    enabled: state.enabled,
    transport: derivedTransport,
  });
  const expectedReady = state.configured
    && state.enabled
    && (derivedTransport === 'https' || derivedTransport === 'local_http');
  if (
    derivedTransport === 'invalid'
    || state.transport !== derivedTransport
    || state.configured !== state.password_configured
    || (!state.configured && state.enabled)
    || state.ready !== expectedReady
    || state.status !== expectedStatus
  ) {
    throw invalidStateError();
  }

  return {
    configured: state.configured,
    enabled: state.enabled,
    ready: state.ready,
    passwordConfigured: state.password_configured,
    status: state.status,
    username: state.username,
    endpoint: state.endpoint,
    transport: state.transport,
  };
}

export function assertAgentMcpConfigurePostcondition(state, submittedUsername) {
  const expectedUsername = normalizeAgentMcpUsername(submittedUsername);
  if (
    !isRecord(state)
    || state.configured !== true
    || state.passwordConfigured !== true
    || state.enabled !== true
    || state.ready !== true
    || state.status !== 'ready'
    || (state.transport !== 'https' && state.transport !== 'local_http')
    || state.username !== expectedUsername
    || deriveAgentMcpTransport(state.endpoint) !== state.transport
  ) {
    const error = new Error('AI access configuration could not be verified.');
    error.code = 'AGENT_MCP_CONFIGURE_POSTCONDITION_FAILED';
    throw error;
  }

  return state;
}

export function createBridgeAgentMcpApi({ client = new BridgeClient() } = {}) {
  const request = async (action, payload) => normalizeAgentMcpState(
    await client.sendMessageWithResponse(action, payload),
  );

  return {
    getStatus: () => request('agent_mcp.status', {}),
    configure: async ({ username, password }) => {
      const normalizedUsername = normalizeAgentMcpUsername(username);
      const state = await request('agent_mcp.configure', {
        username: normalizedUsername,
        password,
      });
      return assertAgentMcpConfigurePostcondition(state, normalizedUsername);
    },
    setEnabled: enabled => request('agent_mcp.set_enabled', { enabled }),
    revoke: () => request('agent_mcp.revoke', {}),
  };
}
